import { useState } from "react";
import { Send, CheckCircle } from "lucide-react";
import { useLang } from "@/components/LanguageContext";

const translations = {
  es: {
    badge: "Presupuesto Gratis",
    title: "Solicita tu Presupuesto Personalizado",
    titleHighlight: "en 60 Segundos",
    sub: "Sin compromiso. Te respondemos en menos de 2 horas.",
    name: "Nombre Completo *",
    namePlaceholder: "Ej: María García",
    phone: "WhatsApp / Teléfono *",
    phonePlaceholder: "Ej: +1 786 767 2593",
    email: "Email *",
    emailPlaceholder: "Ej: tu@email.com",
    service: "Tipo de Servicio *",
    servicePlaceholder: "Selecciona un servicio...",
    services: {
      "residencial-deep": "🏠 Limpieza Residencial – Deep Cleaning",
      "residencial-regular": "🧹 Limpieza Residencial – Regular",
      "mudanza": "📦 Limpieza de Mudanza",
      "organizacion": "✨ Organización del Hogar",
    },
    message: "Mensaje Adicional (Opcional)",
    messagePlaceholder: "Cuéntanos más sobre tu espacio, tamaño, frecuencia deseada...",
    submit: "¡Enviar Solicitud y Recibir Descuento!",
    sending: "Enviando...",
    privacy: "🔒 Tus datos están seguros. No hacemos spam. Solo te contactamos para tu presupuesto.",
    successTitle: "¡Solicitud Recibida!",
    successMsg: (name, phone) => `Gracias, ${name}. Nuestro equipo se pondrá en contacto contigo en menos de 2 horas con tu presupuesto personalizado y tu 20% de descuento aplicado.`,
    successPhone: (phone) => `Revisa también tu WhatsApp: ${phone}`,
  },
  en: {
    badge: "Free Quote",
    title: "Get Your Personalized Quote",
    titleHighlight: "in 60 Seconds",
    sub: "No commitment. We'll get back to you in less than 2 hours.",
    name: "Full Name *",
    namePlaceholder: "E.g: Maria Garcia",
    phone: "WhatsApp / Phone *",
    phonePlaceholder: "E.g: +1 786 767 2593",
    email: "Email *",
    emailPlaceholder: "E.g: you@email.com",
    service: "Service Type *",
    servicePlaceholder: "Select a service...",
    services: {
      "residencial-deep": "🏠 Residential Cleaning – Deep Cleaning",
      "residencial-regular": "🧹 Residential Cleaning – Regular",
      "mudanza": "📦 Move-In / Move-Out Cleaning",
      "organizacion": "✨ Home Organization",
    },
    message: "Additional Message (Optional)",
    messagePlaceholder: "Tell us more about your space, size, desired frequency...",
    submit: "Send Request & Get My Discount!",
    sending: "Sending...",
    privacy: "🔒 Your data is safe. No spam. We only contact you for your quote.",
    successTitle: "Request Received!",
    successMsg: (name) => `Thank you, ${name}. Our team will contact you within 2 hours with your personalized quote and your 20% discount applied.`,
    successPhone: (phone) => `Also check your WhatsApp: ${phone}`,
  },
};

export default function LeadForm() {
  const { lang } = useLang();
  const tx = translations[lang];
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const htmlBody = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; background: #f8fafc; padding: 30px; border-radius: 12px;">
        <div style="background: #15803d; padding: 20px; border-radius: 8px 8px 0 0; text-align: center;">
          <h1 style="color: white; margin: 0; font-size: 22px;">✨ Nueva Solicitud de Presupuesto</h1>
          <p style="color: #bbf7d0; margin: 5px 0 0;">IvisS Cleaning Company</p>
        </div>
        <div style="background: white; padding: 25px; border-radius: 0 0 8px 8px; border: 1px solid #e2e8f0;">
          <table style="width: 100%; border-collapse: collapse;">
            <tr><td style="padding: 10px 0; border-bottom: 1px solid #f1f5f9; color: #64748b; width: 40%;">👤 Nombre</td><td style="padding: 10px 0; border-bottom: 1px solid #f1f5f9; font-weight: bold; color: #1e293b;">${form.name}</td></tr>
            <tr><td style="padding: 10px 0; border-bottom: 1px solid #f1f5f9; color: #64748b;">📱 WhatsApp</td><td style="padding: 10px 0; border-bottom: 1px solid #f1f5f9; font-weight: bold; color: #1e293b;">${form.phone}</td></tr>
            <tr><td style="padding: 10px 0; border-bottom: 1px solid #f1f5f9; color: #64748b;">📧 Email</td><td style="padding: 10px 0; border-bottom: 1px solid #f1f5f9; font-weight: bold; color: #1e293b;">${form.email}</td></tr>
            <tr><td style="padding: 10px 0; border-bottom: 1px solid #f1f5f9; color: #64748b;">🧹 Servicio</td><td style="padding: 10px 0; border-bottom: 1px solid #f1f5f9; font-weight: bold; color: #1e293b;">${tx.services[form.service] || form.service}</td></tr>
            ${form.message ? `<tr><td style="padding: 10px 0; color: #64748b; vertical-align: top;">💬 Mensaje</td><td style="padding: 10px 0; color: #1e293b;">${form.message}</td></tr>` : ""}
          </table>
          <div style="margin-top: 20px; background: #dcfce7; border: 1px solid #86efac; border-radius: 8px; padding: 12px; text-align: center;">
            <strong style="color: #15803d;">⚡ Responder en menos de 2 horas para aplicar el 20% de descuento</strong>
          </div>
        </div>
        <p style="text-align: center; color: #94a3b8; font-size: 12px; margin-top: 15px;">IvisS Cleaning Company · 14150 Wunderlich Dr, Houston, TX 77069</p>
      </div>
    `;

    await axios.post(
      `${"https://api.agentui.ai/proxy"}/emails/send`,
      {
        to: ["cuba52796@gmail.com", "Ivisscleaning@gmail.com"],
        subject: `🧹 Nueva Solicitud de Presupuesto - ${form.name}`,
        html: htmlBody,
        text: `Nueva solicitud de: ${form.name} | Tel: ${form.phone} | Email: ${form.email} | Servicio: ${form.service} | Mensaje: ${form.message}`,
      },
      { headers: { "x-api-key": window.config.apiKey } }
    );

    setLoading(false);
    setSubmitted(true);
  };

  return (
    <section id="formulario" className="bg-white py-20 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-10">
          <span className="text-green-600 font-semibold text-sm uppercase tracking-widest">{tx.badge}</span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2">
            {tx.title} <span className="text-green-600">{tx.titleHighlight}</span>
          </h2>
          <p className="text-gray-500 mt-3">{tx.sub}</p>
        </div>

        {submitted ? (
          <div className="bg-green-50 border border-green-200 rounded-2xl p-10 text-center flex flex-col items-center gap-4">
            <CheckCircle className="w-16 h-16 text-green-500" />
            <h3 className="text-2xl font-bold text-gray-900">{tx.successTitle}</h3>
            <p className="text-gray-600 max-w-md">{tx.successMsg(form.name)} <span className="text-green-600 font-bold">20%</span>.</p>
            <p className="text-sm text-gray-400">{tx.successPhone(form.phone)}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-white border-2 border-gray-100 hover:border-green-200 rounded-2xl shadow-xl p-8 flex flex-col gap-5 transition-colors duration-300">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="flex flex-col gap-1">
                <label className="text-sm font-semibold text-gray-800">{tx.name}</label>
                <input
                  name="name" required value={form.name} onChange={handleChange}
                  placeholder={tx.namePlaceholder}
                  className="border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-green-400 bg-white transition-all"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-semibold text-gray-800">{tx.phone}</label>
                <input
                  name="phone" required value={form.phone} onChange={handleChange}
                  placeholder={tx.phonePlaceholder}
                  className="border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-green-400 bg-white transition-all"
                />
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-semibold text-gray-800">{tx.email}</label>
              <input
                name="email" type="email" required value={form.email} onChange={handleChange}
                placeholder={tx.emailPlaceholder}
                className="border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-green-400 bg-white transition-all"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-semibold text-gray-800">{tx.service}</label>
              <select
                name="service" required value={form.service} onChange={handleChange}
                className="border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-green-400 bg-white text-gray-700 transition-all"
              >
                <option value="">{tx.servicePlaceholder}</option>
                {Object.entries(tx.services).map(([val, label]) => (
                  <option key={val} value={val}>{label}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-semibold text-gray-800">{tx.message}</label>
              <textarea
                name="message" value={form.message} onChange={handleChange} rows={3}
                placeholder={tx.messagePlaceholder}
                className="border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-green-400 bg-white resize-none transition-all"
              />
            </div>
            <button
              type="submit" disabled={loading}
              className="bg-green-600 hover:bg-green-500 text-white font-bold py-4 px-8 rounded-xl text-lg transition-all duration-200 flex items-center justify-center gap-2 shadow-lg shadow-green-200 hover:shadow-green-300 hover:scale-[1.02] disabled:opacity-70"
            >
              {loading ? (
                <span className="flex items-center gap-2"><span className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full inline-block" /> {tx.sending}</span>
              ) : (
                <><Send className="w-5 h-5" /> {tx.submit}</>
              )}
            </button>
            <p className="text-xs text-center text-gray-400">{tx.privacy}</p>
          </form>
        )}
      </div>
    </section>
  );
}